import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { orderService } from '../../services/order.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PackageIcon, TruckIcon } from '../../components/icons/Icons';

const STATUS_TABS = [
  { key: '', label: 'All Orders' },
  { key: 'pending', label: 'Pending' },
  { key: 'processing', label: 'Processing' },
  { key: 'packed', label: 'Packed' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' },
];

const FULFILLMENT_OPTIONS = [
  { value: 'processing', label: 'Processing' },
  { value: 'packed', label: 'Packed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
];

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span style={{
      display: 'inline-block', padding: '4px 12px', borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold,
      color: '#fff', backgroundColor: color,
    }}>{label}</span>
  );
};

const SellerOrders = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [updatingId, setUpdatingId] = useState(null);

  const fetchOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (activeTab) params.fulfillment_status = activeTab;
      const response = await orderService.getSellerOrders(params);
      const data = response.data?.data || response.data;
      setOrderItems(data.orderItems || []);
      setPagination({ page: data.pagination?.page || 1, totalPages: data.pagination?.totalPages || 1 });
    } catch (error) {
      console.error('Failed to fetch seller orders:', error);
      setOrderItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchOrders(1); }, [fetchOrders]);

  const handleStatusUpdate = async (orderItemId, newStatus) => {
    setUpdatingId(orderItemId);
    try {
      await orderService.updateFulfillmentStatus(orderItemId, newStatus);
      toast.success('Fulfillment status updated');
      // Update locally
      setOrderItems(prev => prev.map(item =>
        item.id === orderItemId ? { ...item, fulfillment_status: newStatus } : item
      ));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.xl, flexWrap: 'wrap', gap: theme.spacing.md }}>
          <h1 style={{
            fontSize: isMobile ? theme.typography.fontSize['2xl'] : theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            <TruckIcon size={28} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            My Orders
          </h1>
        </div>

        {/* Status Filter Tabs */}
        <div style={{
          display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.xl,
          overflowX: 'auto', paddingBottom: theme.spacing.sm,
        }}>
          {STATUS_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              borderRadius: theme.borderRadius.full,
              border: `1px solid ${activeTab === tab.key ? theme.colors.primary.main : theme.colors.gray[300]}`,
              backgroundColor: activeTab === tab.key ? theme.colors.primary.main : 'transparent',
              color: activeTab === tab.key ? '#fff' : theme.colors.text.secondary,
              fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium,
              cursor: 'pointer', whiteSpace: 'nowrap', transition: theme.transitions.fast,
            }}>{tab.label}</button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : orderItems.length === 0 ? (
          <Card padding="xl">
            <div style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
              <PackageIcon size={64} color={theme.colors.gray[300]} />
              <h3 style={{ marginTop: theme.spacing.lg, color: theme.colors.text.secondary }}>No orders found</h3>
              <p style={{ color: theme.colors.text.disabled }}>
                {activeTab ? 'No orders match this filter.' : 'No orders for your products yet.'}
              </p>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            {orderItems.map(item => (
              <Card key={item.id} padding="lg">
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: theme.spacing.md,
                  alignItems: isMobile ? 'stretch' : 'center',
                }}>
                  {/* Product Info */}
                  <div style={{ display: 'flex', gap: theme.spacing.md, flex: 1 }}>
                    {item.product_image ? (
                      <img src={item.product_image} alt={item.product_name}
                        style={{ width: 64, height: 64, borderRadius: theme.borderRadius.md, objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: 64, height: 64, borderRadius: theme.borderRadius.md,
                        backgroundColor: theme.colors.gray[200], display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}><PackageIcon size={24} color={theme.colors.gray[400]} /></div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: 2 }}>{item.product_name}</div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                        Qty: {item.quantity} × {formatCurrency(item.price)}
                      </div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled, marginTop: 2 }}>
                        Order #{item.order?.order_number} • {formatDate(item.order?.createdAt || item.order?.created_at)}
                      </div>
                      {item.order?.user && (
                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>
                          Customer: {item.order.user.first_name} {item.order.user.last_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Amount */}
                  <div style={{ textAlign: isMobile ? 'left' : 'center', minWidth: 90 }}>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>Amount</div>
                    <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.lg }}>
                      {formatCurrency(item.subtotal)}
                    </div>
                  </div>

                  {/* Status + Update */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, minWidth: 180, alignItems: isMobile ? 'stretch' : 'flex-end' }}>
                    <StatusBadge status={item.fulfillment_status} />
                    {item.fulfillment_status !== 'delivered' && item.fulfillment_status !== 'cancelled' && (
                      <select
                        value={item.fulfillment_status}
                        onChange={(e) => handleStatusUpdate(item.id, e.target.value)}
                        disabled={updatingId === item.id}
                        style={{
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          borderRadius: theme.borderRadius.md,
                          border: `1px solid ${theme.colors.gray[300]}`,
                          fontSize: theme.typography.fontSize.xs,
                          backgroundColor: theme.colors.white,
                          cursor: 'pointer',
                          opacity: updatingId === item.id ? 0.5 : 1,
                        }}
                      >
                        {FULFILLMENT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                    <Button variant="outline" size="sm"
                      onClick={() => navigate(`/seller/orders/${item.order?.id || item.order_id}`)}>
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
                <Button variant="outline" size="sm" disabled={pagination.page <= 1}
                  onClick={() => fetchOrders(pagination.page - 1)}>Previous</Button>
                <span style={{ alignSelf: 'center', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchOrders(pagination.page + 1)}>Next</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SellerOrders;
